import React, { useState } from "react";
import {
  Box,
  Button,
  Checkbox,
  Chip,
  CircularProgress,
  Dialog,
  DialogActions,
  DialogContent,
  DialogTitle,
  IconButton,
  ListItemIcon,
  ListItemText,
  Menu,
  MenuItem,
  Paper,
  Select,
  Table,
  TableBody,
  TableCell,
  TableContainer,
  TableHead,
  TableRow,
  TextField,
  Tooltip,
} from "@mui/material";
import {
  Add as AddIcon,
  Edit as EditIcon,
  Delete as DeleteIcon,
  CheckCircleOutline as ApproveIcon,
  HighlightOff as DisapproveIcon,
  AttachFile as AttachFileIcon,
  OpenInNew as OpenInNewIcon,
  LinkOff as LinkOffIcon,
} from "@mui/icons-material";
import { updateTravelCalendarEntryApi } from "../../../components/api/TravelCalendarApiService";
import toast from "react-hot-toast";
import { format, getDay, isWeekend, isToday } from "date-fns";

const DAY_NAMES = ["Sunday", "Monday", "Tuesday", "Wednesday", "Thursday", "Friday", "Saturday"];

// Extract HH:mm from a datetime string without timezone conversion
const extractTime = (dateTimeStr) => {
  if (!dateTimeStr) return "";
  const str = String(dateTimeStr);
  const match = str.match(/T(\d{2}:\d{2})/);
  if (match) return match[1];
  const timeOnly = str.match(/^(\d{2}:\d{2})/);
  if (timeOnly) return timeOnly[1];
  return "";
};

// Row background color
const getRowStyle = (date) => {
  if (isWeekend(date)) return { backgroundColor: "#2E86AB", color: "#fff" };
  if (isToday(date)) return { backgroundColor: "#FFF9C4" };
  return {};
};

const CalendarTable = ({
  daysInMonth,
  entriesByDate,
  sharedOccasions,
  selectedUserId,
  showCheckboxes,
  selectedEntryIds,
  allSelected,
  multiEditMode,
  editableData,
  isOwnCalendar,
  canEdit,
  isAdminOrManager,
  isManager,
  onToggleSelectAll,
  onToggleBulkSelect,
  onMultiEditCellChange,
  isRowDirty,
  onEditEntry,
  onDeleteEntry,
  onAddEntry,
  onApproval,
  onOpenDisapprove,
  onInspectEntry,
  onLinkSaved,
  onDateOverview,
}) => {
  const getEntriesForDate = (date) => entriesByDate[format(date, "yyyy-MM-dd")] || [];

  const getSharedOccasionsForDate = (date) => {
    const dateStr = format(date, "yyyy-MM-dd");
    return sharedOccasions.filter((e) => {
      const entryDateStr = e.entryDate ? format(new Date(e.entryDate), "yyyy-MM-dd") : "";
      return entryDateStr === dateStr && e.user?.id !== selectedUserId;
    });
  };

  return (
    <Paper>
      <TableContainer sx={{ maxHeight: "calc(100vh - 260px)" }} data-screenshot-table>
        <Table stickyHeader size="small" sx={{ tableLayout: "fixed" }}>
          <TableHead>
            <TableRow>
              {showCheckboxes && (
                <TableCell
                  align="center"
                  sx={{
                    fontWeight: "bold",
                    backgroundColor: "#2e86ab",
                    color: "#fff",
                    width: 42,
                    position: "sticky",
                    top: 0,
                    zIndex: 2,
                    p: 0,
                  }}
                >
                  <Checkbox
                    size="small"
                    checked={allSelected}
                    indeterminate={selectedEntryIds.length > 0 && !allSelected}
                    onChange={onToggleSelectAll}
                    sx={{ color: "#fff", "&.Mui-checked": { color: "#fff" }, "&.MuiCheckbox-indeterminate": { color: "#fff" } }}
                  />
                </TableCell>
              )}
              {[
                { label: "Date", width: 145, bg: "#2e86ab" },
                { label: "Day", width: 90, bg: "#2e86ab" },
                { label: "Destination", width: 130, bg: "#20315d" },
                { label: "Flight", width: 100, bg: "#20315d" },
                { label: "ETD", width: 70, bg: "#20315d" },
                { label: "ETA", width: 70, bg: "#20315d" },
                { label: "Comment", width: 180, bg: "#20315d" },
                { label: "Others", width: 150, bg: "#20315d" },
                { label: "EU", width: 55, bg: "#90a4ae" },
                { label: "Sin", width: 55, bg: "#90a4ae" },
                { label: "Special Occasions", width: 160, bg: "#20315d" },
                { label: "Status", width: 80, bg: "#455a64" },
                { label: "Type", width: 80, bg: "#455a64" },
                { label: "Category", width: 110, bg: "#455a64" },
                { label: "Actions", width: 90, bg: "#20315d" },
              ].map((col) => (
                <TableCell
                  key={col.label}
                  align="center"
                  sx={{
                    fontWeight: "bold",
                    backgroundColor: col.bg,
                    color: "#fff",
                    width: col.width,
                    position: "sticky",
                    top: 0,
                    zIndex: 2,
                  }}
                >
                  {col.label}
                </TableCell>
              ))}
            </TableRow>
          </TableHead>
          <TableBody>
            {daysInMonth.map((date) => {
              const dateEntries = getEntriesForDate(date);
              const rowStyle = getRowStyle(date);
              const weekend = isWeekend(date);
              const today = isToday(date);
              const dateStr = format(date, "yyyy-MM-dd");
              const editRow = multiEditMode ? editableData[dateStr] : null;

              return (
                <CalendarRow
                  key={date.toISOString()}
                  date={date}
                  dateStr={dateStr}
                  entries={dateEntries}
                  rowStyle={rowStyle}
                  weekend={weekend}
                  today={today}
                  editRow={editRow}
                  multiEditMode={multiEditMode}
                  showCheckboxes={showCheckboxes}
                  selectedEntryIds={selectedEntryIds}
                  isOwnCalendar={isOwnCalendar}
                  canEdit={canEdit}
                  isAdminOrManager={isAdminOrManager}
                  isManager={isManager}
                  sharedOccasions={getSharedOccasionsForDate(date)}
                  isRowDirty={isRowDirty}
                  onToggleBulkSelect={onToggleBulkSelect}
                  onMultiEditCellChange={onMultiEditCellChange}
                  onEditEntry={onEditEntry}
                  onDeleteEntry={onDeleteEntry}
                  onAddEntry={onAddEntry}
                  onApproval={onApproval}
                  onOpenDisapprove={onOpenDisapprove}
                  onInspectEntry={onInspectEntry}
                  onLinkSaved={onLinkSaved}
                  onDateOverview={onDateOverview}
                />
              );
            })}
          </TableBody>
        </Table>
      </TableContainer>
    </Paper>
  );
};

// --- Attachment button component ---
const AttachmentButton = ({ entry, weekend, onLinkSaved }) => {
  const [menuAnchor, setMenuAnchor] = useState(null);
  const [manageOpen, setManageOpen] = useState(false);
  const [linkValue, setLinkValue] = useState("");
  const [saving, setSaving] = useState(false);

  const hasLink = Boolean(entry?.sharepointLink);

  const handleButtonClick = (e) => setMenuAnchor(e.currentTarget);
  const handleClose = () => setMenuAnchor(null);

  const handleOpenLink = () => {
    handleClose();
    window.open(entry.sharepointLink, "_blank", "noopener,noreferrer");
  };

  const handleOpenManage = () => {
    handleClose();
    setLinkValue(entry?.sharepointLink || "");
    setManageOpen(true);
  };

  const handleSave = () => {
    setSaving(true);
    updateTravelCalendarEntryApi(entry.id, { ...entry, sharepointLink: linkValue || null })
      .then(() => {
        setManageOpen(false);
        toast.success(linkValue ? "Link saved." : "Link removed.");
        onLinkSaved?.();
      })
      .catch(() => toast.error("Failed to save link."))
      .finally(() => setSaving(false));
  };

  return (
    <>
      <Tooltip title={entry?.id ? (hasLink ? "Open / manage link" : "Add link") : "No entry"} arrow placement="top">
        <span>
          <IconButton
            size="small"
            onClick={handleButtonClick}
            disabled={!entry?.id}
            sx={{
              p: 0.2,
              color: !entry?.id
                ? (weekend ? "rgba(255,255,255,0.3)" : "rgba(0,0,0,0.2)")
                : hasLink
                ? (weekend ? "#ffe082" : "#1565c0")
                : (weekend ? "rgba(255,255,255,0.5)" : "rgba(0,0,0,0.3)"),
            }}
          >
            <AttachFileIcon sx={{ fontSize: 15 }} />
          </IconButton>
        </span>
      </Tooltip>

      {/* Dropdown menu */}
      <Menu anchorEl={menuAnchor} open={Boolean(menuAnchor)} onClose={handleClose} PaperProps={{ sx: { minWidth: 170 } }}>
        <MenuItem onClick={handleOpenLink} disabled={!hasLink}>
          <ListItemIcon><OpenInNewIcon fontSize="small" /></ListItemIcon>
          <ListItemText primary="Open link" secondary={!hasLink ? "No link set" : null} />
        </MenuItem>
        <MenuItem onClick={handleOpenManage}>
          <ListItemIcon><AttachFileIcon fontSize="small" /></ListItemIcon>
          <ListItemText primary="Manage link" />
        </MenuItem>
      </Menu>

      {/* Manage dialog */}
      <Dialog open={manageOpen} onClose={() => setManageOpen(false)} maxWidth="sm" fullWidth>
        <DialogTitle sx={{ pb: 1 }}>{hasLink ? "Edit Link" : "Add Link"}</DialogTitle>
        <DialogContent sx={{ pt: "12px !important" }}>
          <TextField
            label="SharePoint / Attachment URL"
            value={linkValue}
            onChange={(e) => setLinkValue(e.target.value)}
            fullWidth
            size="small"
            placeholder="https://..."
            autoFocus
          />
        </DialogContent>
        <DialogActions sx={{ justifyContent: "space-between", px: 3, pb: 2 }}>
          <Box>
            {hasLink && (
              <Button startIcon={<LinkOffIcon />} color="error" size="small" onClick={() => { setLinkValue(""); }} disabled={saving}>
                Clear
              </Button>
            )}
          </Box>
          <Box sx={{ display: "flex", gap: 1 }}>
            <Button size="small" onClick={() => setManageOpen(false)} disabled={saving}>Cancel</Button>
            <Button size="small" variant="contained" onClick={handleSave} disabled={saving}>
              {saving ? <CircularProgress size={14} /> : "Save"}
            </Button>
          </Box>
        </DialogActions>
      </Dialog>
    </>
  );
};

// --- Individual row component ---
const CalendarRow = React.memo(({
  date,
  dateStr,
  entries,
  rowStyle,
  weekend,
  today,
  editRow,
  multiEditMode,
  showCheckboxes,
  selectedEntryIds,
  isOwnCalendar,
  canEdit,
  isAdminOrManager,
  isManager,
  sharedOccasions,
  isRowDirty,
  onToggleBulkSelect,
  onMultiEditCellChange,
  onEditEntry,
  onDeleteEntry,
  onAddEntry,
  onApproval,
  onOpenDisapprove,
  onInspectEntry,
  onLinkSaved,
  onDateOverview,
}) => {
  const textFieldSx = (isWeekend) => ({
    "& .MuiInput-underline:before": {
      borderColor: isWeekend ? "rgba(255,255,255,0.3)" : "rgba(0,0,0,0.2)",
    },
  });

  const textInputStyle = (isWeekend, fontSize = "0.75rem") => ({
    fontSize,
    padding: "2px 4px",
    textAlign: "center",
    color: isWeekend ? "#fff" : "inherit",
  });

  const timeInputStyle = (isWeekend) => ({
    fontSize: "0.7rem",
    padding: "2px 0",
    textAlign: "center",
    color: isWeekend ? "#fff" : "inherit",
  });

  // Derive primary and secondary entries from the entries array
  const entry = entries[0] || null;
  const secondEntry = entries[1] || null;
  const hasComplement = entries.length === 1 && entry?.halfDayType && entry.halfDayType !== "NONE";

  const truncateSx = {
    overflow: "hidden",
    textOverflow: "ellipsis",
    whiteSpace: "nowrap",
    maxWidth: 0,
  };

  // Add options for halfDayType
  const halfDayTypeOptions = [
    { value: "NONE", label: "Full Day" },
    { value: "FIRST", label: "First Half ☀️" },
    { value: "LAST", label: "Second Half 🌙" },
  ];

  return (
    <TableRow
      sx={{
        ...rowStyle,
        "&:hover": { backgroundColor: weekend ? "#256d8e" : "#f5f5f5" },
        borderLeft: today ? "4px solid #FFD700" : "none",
      }}
    >
      {/* Bulk select checkbox */}
      {showCheckboxes && (
        <TableCell align="center" sx={{ p: 0 }}>
          {entry && (
            <Checkbox
              size="small"
              checked={selectedEntryIds.includes(entry.id)}
              onChange={() => onToggleBulkSelect(entry.id)}
              sx={{
                color: weekend ? "#fff" : undefined,
                "&.Mui-checked": { color: weekend ? "#fff" : undefined },
              }}
            />
          )}
        </TableCell>
      )}

      {/* Date & Half Day Type */}
      <TableCell
        align="center"
        sx={{
          fontWeight: "bold",
          backgroundColor: undefined,
          position: 'relative',
          width: 145,
          height: 36,
          p: 0,
        }}
      >
        <Box sx={{ display: 'flex', alignItems: 'center', justifyContent: 'center', height: 36, width: '100%', gap: 0.5 }}>
          <AttachmentButton entry={entry} weekend={weekend} onLinkSaved={onLinkSaved} />
          <Tooltip title="View all users' entries" arrow placement="top">
            <span
              style={{ whiteSpace: 'nowrap', fontVariantNumeric: 'tabular-nums', cursor: onDateOverview ? 'pointer' : 'default', textDecoration: onDateOverview ? 'underline dotted' : 'none' }}
              onClick={onDateOverview ? () => onDateOverview(date) : undefined}
            >
              {format(date, "dd MMM")}
            </span>
          </Tooltip>
          {multiEditMode ? (
            editRow && editRow.length > 1 ? (
              <Box sx={{ display: 'flex', gap: 0.3 }}>
                <Chip icon={<span style={{ fontSize: '0.95em', marginLeft: 0 }}>☀️</span>} size="small" sx={{ height: 20, backgroundColor: '#fffde7', color: '#444', fontWeight: 500, borderRadius: 2, px: 0.5, boxShadow: 0, '.MuiChip-icon': { ml: 0, mr: 0.2 } }} />
                <Chip icon={<span style={{ fontSize: '0.95em', marginLeft: 0 }}>🌙</span>} size="small" sx={{ height: 20, backgroundColor: '#e3f2fd', color: '#444', fontWeight: 500, borderRadius: 2, px: 0.5, boxShadow: 0, '.MuiChip-icon': { ml: 0, mr: 0.2 } }} />
              </Box>
            ) : (
              <Select
                variant="standard"
                size="small"
                value={editRow?.[0]?.halfDayType || "NONE"}
                onChange={(e) => onMultiEditCellChange(dateStr, 0, "halfDayType", e.target.value)}
                sx={{ fontSize: '0.68rem', minWidth: 70, ml: 0.5 }}
              >
                {halfDayTypeOptions.map(opt => (
                  <MenuItem key={opt.value} value={opt.value} sx={{ fontSize: '0.68rem' }}>{opt.label}</MenuItem>
                ))}
              </Select>
            )
          ) : secondEntry ? (
            // Two half-day entries — show both chips
            <Box sx={{ display: 'flex', gap: 0.3 }}>
              <Chip
                icon={<span style={{ fontSize: '0.95em', marginLeft: 0 }}>☀️</span>}
                size="small"
                sx={{ height: 20, backgroundColor: '#fffde7', color: '#444', fontWeight: 500, borderRadius: 2, px: 0.5, boxShadow: 0, '.MuiChip-icon': { ml: 0, mr: 0.2 } }}
              />
              <Chip
                icon={<span style={{ fontSize: '0.95em', marginLeft: 0 }}>🌙</span>}
                size="small"
                sx={{ height: 20, backgroundColor: '#e3f2fd', color: '#444', fontWeight: 500, borderRadius: 2, px: 0.5, boxShadow: 0, '.MuiChip-icon': { ml: 0, mr: 0.2 } }}
              />
            </Box>
          ) : (
            entry && entry.halfDayType && entry.halfDayType !== "NONE" ? (
              <Chip
                icon={entry.halfDayType === "FIRST" ? <span style={{fontSize:'0.95em',marginLeft:0}}>☀️</span> : <span style={{fontSize:'0.95em',marginLeft:0}}>🌙</span>}
                size="small"
                sx={{
                  height: 20,
                  backgroundColor: entry.halfDayType === "FIRST" ? '#fffde7' : '#e3f2fd',
                  color: '#444',
                  fontWeight: 500,
                  borderRadius: 2,
                  px: 0.5,
                  boxShadow: 0,
                  '.MuiChip-icon': { ml: 0, mr: 0.2 },
                }}
              />
            ) : (
              <Chip
                label=""
                size="small"
                sx={{
                  height: 20,
                  visibility: 'hidden',
                  px: 0.5,
                }}
              />
            )
          )}
        </Box>
      </TableCell>

      {/* Day */}
      <TableCell
        align="center"
        sx={{
          fontWeight: weekend ? "bold" : "normal",
          color: weekend ? "#fff" : today ? "#d32f2f" : "inherit",
        }}
      >
        {DAY_NAMES[getDay(date)]}
      </TableCell>

      {/* Destination */}
      <TableCell
        align="center"
        sx={{
          color: weekend ? "#fff" : "inherit",
          ...truncateSx,
          p: multiEditMode ? "0 2px" : undefined,
        }}
      >
        {multiEditMode ? (() => {
          const flights = editRow?.[0]?.flights && editRow[0].flights.length > 0
            ? editRow[0].flights
            : [{ destination: editRow?.[0]?.destination || "", flight: editRow?.[0]?.flight || "", etd: editRow?.[0]?.etd || "", eta: editRow?.[0]?.eta || "" }];
          return (
            <Box sx={{ display: "flex", flexDirection: "column", gap: 0.25 }}>
              {flights.map((fl, i) => (
                <Box key={i} sx={{ display: "flex", alignItems: "center", gap: 0.25 }}>
                  <TextField
                    variant="standard"
                    size="small"
                    fullWidth
                    value={fl.destination || ""}
                    onChange={(e) => {
                      const updated = flights.map((f, idx) => idx === i ? { ...f, destination: e.target.value } : f);
                      onMultiEditCellChange(dateStr, 0, "flights", updated);
                    }}
                    inputProps={{ style: textInputStyle(weekend) }}
                    sx={textFieldSx(weekend)}
                  />
                  {flights.length > 1 && (
                    <IconButton size="small" sx={{ p: 0 }} onClick={() => {
                      const updated = flights.filter((_, idx) => idx !== i);
                      onMultiEditCellChange(dateStr, 0, "flights", updated);
                    }}>
                      <DeleteIcon sx={{ fontSize: 11, color: weekend ? "rgba(255,255,255,0.6)" : "rgba(0,0,0,0.35)" }} />
                    </IconButton>
                  )}
                </Box>
              ))}
              <IconButton size="small" sx={{ p: 0, alignSelf: "flex-start" }} onClick={() => {
                onMultiEditCellChange(dateStr, 0, "flights", [...flights, { destination: "", flight: "", etd: "", eta: "" }]);
              }}>
                <AddIcon sx={{ fontSize: 11, color: weekend ? "rgba(255,255,255,0.8)" : "rgba(0,0,0,0.45)" }} />
              </IconButton>
            </Box>
          );
        })() : (() => {
          const flights = entry?.flights && entry.flights.length > 0 ? entry.flights : (entry ? [entry] : []);
          return (
            <Box sx={{ display: "flex", flexDirection: "column", alignItems: "center", gap: 0.25, overflow: "hidden" }}>
              {flights.map((f, i) => (
                <Tooltip key={i} title={f.destination || ""} arrow placement="top" disableHoverListener={!f.destination}>
                  <span style={{ overflow: "hidden", textOverflow: "ellipsis", whiteSpace: "nowrap", maxWidth: "100%", display: "block", fontSize: "0.75rem" }}>{f.destination || ""}</span>
                </Tooltip>
              ))}
            </Box>
          );
        })()}
      </TableCell>

      {/* Flight */}
      <TableCell
        align="center"
        sx={{
          color: weekend ? "#fff" : "inherit",
          ...truncateSx,
          p: multiEditMode ? "0 2px" : undefined,
        }}
      >
        {multiEditMode ? (() => {
          const flights = editRow?.[0]?.flights && editRow[0].flights.length > 0
            ? editRow[0].flights
            : [{ destination: editRow?.[0]?.destination || "", flight: editRow?.[0]?.flight || "", etd: editRow?.[0]?.etd || "", eta: editRow?.[0]?.eta || "" }];
          return (
            <Box sx={{ display: "flex", flexDirection: "column", gap: 0.25 }}>
              {flights.map((fl, i) => (
                <TextField
                  key={i}
                  variant="standard"
                  size="small"
                  fullWidth
                  value={fl.flight || ""}
                  onChange={(e) => {
                    const updated = flights.map((f, idx) => idx === i ? { ...f, flight: e.target.value } : f);
                    onMultiEditCellChange(dateStr, 0, "flights", updated);
                  }}
                  inputProps={{ style: textInputStyle(weekend) }}
                  sx={textFieldSx(weekend)}
                />
              ))}
            </Box>
          );
        })() : (() => {
          const flights = entry?.flights && entry.flights.length > 0 ? entry.flights : (entry ? [entry] : []);
          return (
            <Box sx={{ display: "flex", flexDirection: "column", alignItems: "center", gap: 0.25, overflow: "hidden" }}>
              {flights.map((f, i) => (
                <Tooltip key={i} title={f.flight || ""} arrow placement="top" disableHoverListener={!f.flight}>
                  <span style={{ overflow: "hidden", textOverflow: "ellipsis", whiteSpace: "nowrap", maxWidth: "100%", display: "block", fontSize: "0.75rem" }}>{f.flight || ""}</span>
                </Tooltip>
              ))}
            </Box>
          );
        })()}
      </TableCell>

      {/* ETD */}
      <TableCell align="center" sx={{ color: weekend ? "#fff" : "inherit", p: multiEditMode ? "0 2px" : undefined }}>
        {multiEditMode ? (() => {
          const flights = editRow?.[0]?.flights && editRow[0].flights.length > 0
            ? editRow[0].flights
            : [{ destination: editRow?.[0]?.destination || "", flight: editRow?.[0]?.flight || "", etd: editRow?.[0]?.etd || "", eta: editRow?.[0]?.eta || "" }];
          return (
            <Box sx={{ display: "flex", flexDirection: "column", gap: 0.25 }}>
              {flights.map((fl, i) => (
                <TextField
                  key={i}
                  variant="standard"
                  size="small"
                  fullWidth
                  type="time"
                  value={fl.etd || ""}
                  onChange={(e) => {
                    const updated = flights.map((f, idx) => idx === i ? { ...f, etd: e.target.value } : f);
                    onMultiEditCellChange(dateStr, 0, "flights", updated);
                  }}
                  inputProps={{ style: timeInputStyle(weekend) }}
                  sx={textFieldSx(weekend)}
                />
              ))}
            </Box>
          );
        })() : (() => {
          const flights = entry?.flights && entry.flights.length > 0 ? entry.flights : (entry ? [entry] : []);
          return (
            <Box sx={{ display: "flex", flexDirection: "column", alignItems: "center", gap: 0.25 }}>
              {flights.map((f, i) => (
                <span key={i} style={{ fontSize: "0.75rem" }}>{extractTime(f.etd)}</span>
              ))}
            </Box>
          );
        })()}
      </TableCell>

      {/* ETA */}
      <TableCell align="center" sx={{ color: weekend ? "#fff" : "inherit", p: multiEditMode ? "0 2px" : undefined }}>
        {multiEditMode ? (() => {
          const flights = editRow?.[0]?.flights && editRow[0].flights.length > 0
            ? editRow[0].flights
            : [{ destination: editRow?.[0]?.destination || "", flight: editRow?.[0]?.flight || "", etd: editRow?.[0]?.etd || "", eta: editRow?.[0]?.eta || "" }];
          return (
            <Box sx={{ display: "flex", flexDirection: "column", gap: 0.25 }}>
              {flights.map((fl, i) => (
                <TextField
                  key={i}
                  variant="standard"
                  size="small"
                  fullWidth
                  type="time"
                  value={fl.eta || ""}
                  onChange={(e) => {
                    const updated = flights.map((f, idx) => idx === i ? { ...f, eta: e.target.value } : f);
                    onMultiEditCellChange(dateStr, 0, "flights", updated);
                  }}
                  inputProps={{ style: timeInputStyle(weekend) }}
                  sx={textFieldSx(weekend)}
                />
              ))}
            </Box>
          );
        })() : (() => {
          const flights = entry?.flights && entry.flights.length > 0 ? entry.flights : (entry ? [entry] : []);
          return (
            <Box sx={{ display: "flex", flexDirection: "column", alignItems: "center", gap: 0.25 }}>
              {flights.map((f, i) => (
                <span key={i} style={{ fontSize: "0.75rem" }}>{extractTime(f.eta)}</span>
              ))}
            </Box>
          );
        })()}
      </TableCell>

      {/* Comment */}
      <TableCell
        align="center"
        sx={{
          color: weekend ? "#fff" : "inherit",
          fontWeight: "normal",
          ...truncateSx,
          p: multiEditMode ? "0 2px" : undefined,
        }}
      >
        {multiEditMode ? (
          <TextField
            variant="standard"
            size="small"
            fullWidth
            value={editRow?.[0]?.comment || ""}
            onChange={(e) => onMultiEditCellChange(dateStr, 0, "comment", e.target.value)}
            inputProps={{ style: textInputStyle(weekend) }}
            sx={textFieldSx(weekend)}
          />
        ) : (
          <Tooltip title={entry?.comment || ""} arrow placement="top" disableHoverListener={!entry?.comment}>
            <span>{entry?.comment || ""}</span>
          </Tooltip>
        )}
      </TableCell>

      {/* Others */}
      <TableCell
        align="center"
        sx={{
          color: weekend
            ? "#fff"
            : entry?.others?.toLowerCase().includes("birthday")
            ? "#1565c0"
            : "inherit",
          ...truncateSx,
          p: multiEditMode ? "0 2px" : undefined,
        }}
      >
        {multiEditMode ? (
          <TextField
            variant="standard"
            size="small"
            fullWidth
            value={editRow?.[0]?.others || ""}
            onChange={(e) => onMultiEditCellChange(dateStr, 0, "others", e.target.value)}
            inputProps={{ style: textInputStyle(weekend) }}
            sx={textFieldSx(weekend)}
          />
        ) : (
          <Tooltip title={entry?.others || ""} arrow placement="top" disableHoverListener={!entry?.others}>
            <span>{entry?.others || ""}</span>
          </Tooltip>
        )}
      </TableCell>

      {/* in EU */}
      <TableCell align="center" sx={{ p: multiEditMode ? 0 : undefined }}>
        {multiEditMode ? (
          <Checkbox
            size="small"
            checked={editRow?.[0]?.inEu || false}
            onChange={(e) => onMultiEditCellChange(dateStr, 0, "inEu", e.target.checked)}
            sx={{ p: 0, color: weekend ? "#fff" : undefined, "&.Mui-checked": { color: weekend ? "#fff" : "#4caf50" } }}
          />
        ) : (
          entry?.inEu && (
            <Box
              sx={{
                width: 16,
                height: 16,
                borderRadius: "50%",
                backgroundColor: "#4caf50",
                mx: "auto",
              }}
            />
          )
        )}
      </TableCell>

      {/* in Sin */}
      <TableCell align="center" sx={{ p: multiEditMode ? 0 : undefined }}>
        {multiEditMode ? (
          <Checkbox
            size="small"
            checked={editRow?.[0]?.inSin || false}
            onChange={(e) => onMultiEditCellChange(dateStr, 0, "inSin", e.target.checked)}
            sx={{ p: 0, color: weekend ? "#fff" : undefined, "&.Mui-checked": { color: weekend ? "#fff" : "#2196f3" } }}
          />
        ) : (
          entry?.inSin && (
            <Box
              sx={{
                width: 16,
                height: 16,
                borderRadius: "50%",
                backgroundColor: "#2196f3",
                mx: "auto",
              }}
            />
          )
        )}
      </TableCell>

      {/* Special Occasions */}
      <TableCell
        align="center"
        sx={{
          color: weekend ? "#fff" : "#6a1b9a",
          fontStyle: "italic",
          ...truncateSx,
          p: multiEditMode ? "0 2px" : undefined,
        }}
      >
        {multiEditMode ? (
          <TextField
            variant="standard"
            size="small"
            fullWidth
            value={editRow?.[0]?.specialOccasion || ""}
            onChange={(e) => onMultiEditCellChange(dateStr, 0, "specialOccasion", e.target.value)}
            inputProps={{ style: { ...textInputStyle(weekend), color: weekend ? "#fff" : "#6a1b9a", fontStyle: "italic" } }}
            sx={textFieldSx(weekend)}
          />
        ) : (
          (() => {
            const parts = [];
            if (entry?.specialOccasion) parts.push(entry.specialOccasion);
            if (secondEntry?.specialOccasion && secondEntry.specialOccasion !== entry?.specialOccasion)
              parts.push(secondEntry.specialOccasion);
            sharedOccasions.forEach((s) => {
              const from = s.user?.name || s.user?.username || "";
              parts.push(`${s.specialOccasion} (${from})`);
            });
            const text = parts.join(" | ");
            return (
              <Tooltip title={text} arrow placement="top" disableHoverListener={!text}>
                <span>{text}</span>
              </Tooltip>
            );
          })()
        )}
      </TableCell>

      {/* Status */}
      <TableCell align="center">
        <Box sx={{ display: "flex", flexDirection: "column", alignItems: "center", gap: 0.4 }}>
          {[entry, secondEntry].filter(Boolean).map((e) =>
            e.entryType === "ACTUAL" ? (
              <Tooltip
                key={e.id}
                title={
                  e.approvalStatus === "NOT_APPROVED" && e.disapprovalNote
                    ? `Note: ${e.disapprovalNote} (click for details)`
                    : "Click to view details"
                }
                arrow
                placement="top"
              >
                <Chip
                  label={
                    e.approvalStatus === "APPROVED"
                      ? "Approved"
                      : e.approvalStatus === "NOT_APPROVED"
                      ? "Not Approved"
                      : "Waiting"
                  }
                  size="small"
                  onClick={() => onInspectEntry(e, date)}
                  sx={{
                    fontSize: "0.7rem",
                    height: 22,
                    cursor: "pointer",
                    backgroundColor:
                      e.approvalStatus === "APPROVED"
                        ? "#e8f5e9"
                        : e.approvalStatus === "NOT_APPROVED"
                        ? "#ffebee"
                        : "#fff8e1",
                    color:
                      e.approvalStatus === "APPROVED"
                        ? "#2e7d32"
                        : e.approvalStatus === "NOT_APPROVED"
                        ? "#c62828"
                        : "#f57f17",
                    fontWeight: "bold",
                  }}
                />
              </Tooltip>
            ) : null
          )}
        </Box>
      </TableCell>

      {/* Type */}
      <TableCell align="center" sx={{ p: multiEditMode ? "0 2px" : undefined }}>
        {multiEditMode ? (
          <Box sx={{ display: "flex", flexDirection: "column", gap: 0.3 }}>
            {editRow && editRow.map((row, i) => (
              <Box key={i} sx={{ display: "flex", alignItems: "center", gap: 0.3 }}>
                {editRow.length > 1 && (
                  <span style={{ fontSize: "0.75rem", flexShrink: 0 }}>{i === 0 ? "☀️" : "🌙"}</span>
                )}
                <Select
                  variant="standard"
                  size="small"
                  value={row.entryType || "TEMPLATE"}
                  onChange={(e) => onMultiEditCellChange(dateStr, i, "entryType", e.target.value)}
                  sx={{ fontSize: "0.7rem", color: weekend ? "#fff" : "inherit", "& .MuiSelect-icon": { color: weekend ? "#fff" : undefined } }}
                >
                  <MenuItem value="ACTUAL" sx={{ fontSize: "0.75rem" }}>Actual</MenuItem>
                  <MenuItem value="TEMPLATE" sx={{ fontSize: "0.75rem" }}>Template</MenuItem>
                </Select>
              </Box>
            ))}
          </Box>
        ) : (
          entry && (
            <Chip
              label={entry.entryType === "TEMPLATE" ? "Template" : "Actual"}
              size="small"
              variant="outlined"
              sx={{
                fontSize: "0.7rem",
                height: 22,
                borderColor: entry.entryType === "TEMPLATE" ? "#7b1fa2" : "#1565c0",
                color: entry.entryType === "TEMPLATE" ? "#7b1fa2" : "#1565c0",
              }}
            />
          )
        )}
      </TableCell>

      {/* Category */}
      <TableCell align="center" sx={{ p: multiEditMode ? "0 2px" : undefined }}>
        {multiEditMode ? (
          <Box sx={{ display: "flex", flexDirection: "column", gap: 0.3 }}>
            {editRow && editRow.map((row, i) => (
              <Box key={i} sx={{ display: "flex", alignItems: "center", gap: 0.3 }}>
                {editRow.length > 1 && (
                  <span style={{ fontSize: "0.75rem", flexShrink: 0 }}>{i === 0 ? "☀️" : "🌙"}</span>
                )}
                <Select
                  variant="standard"
                  size="small"
                  value={row.category || ""}
                  onChange={(e) => onMultiEditCellChange(dateStr, i, "category", e.target.value)}
                  displayEmpty
                  sx={{ fontSize: "0.7rem", minWidth: 90, color: weekend ? "#fff" : "inherit", "& .MuiSelect-icon": { color: weekend ? "#fff" : undefined } }}
                >
                  <MenuItem value="" sx={{ fontSize: "0.75rem" }}><em>None</em></MenuItem>
                  <MenuItem value="ANNUAL_LEAVE" sx={{ fontSize: "0.75rem" }}>Annual Leave</MenuItem>
                  <MenuItem value="BUSINESS_TRIP" sx={{ fontSize: "0.75rem" }}>Business Trip</MenuItem>
                  <MenuItem value="NATIONAL_HOLIDAY" sx={{ fontSize: "0.75rem" }}>National Holiday</MenuItem>
                  <MenuItem value="COMPENSATION" sx={{ fontSize: "0.75rem" }}>Compensation</MenuItem>
                  <MenuItem value="OTHER" sx={{ fontSize: "0.75rem" }}>Other</MenuItem>
                </Select>
              </Box>
            ))}
          </Box>
        ) : (
          <Box sx={{ display: "flex", flexDirection: "column", alignItems: "center", gap: 0.3 }}>
            {[entry, secondEntry].filter(Boolean).map((e) =>
              e.category ? (
                <Chip
                  key={e.id}
                  label={{
                    ANNUAL_LEAVE: "Ann. Leave",
                    BUSINESS_TRIP: "Biz Trip",
                    NATIONAL_HOLIDAY: "Nat. Holiday",
                    COMPENSATION: "Compensation",
                    OTHER: "Other",
                  }[e.category] || e.category}
                  size="small"
                  sx={{
                    fontSize: "0.65rem",
                    height: 20,
                    backgroundColor: {
                      ANNUAL_LEAVE: "#7b1fa2",
                      BUSINESS_TRIP: "#1565c0",
                      NATIONAL_HOLIDAY: "#2e7d32",
                      COMPENSATION: "#e65100",
                      OTHER: "#546e7a",
                    }[e.category] || "#90a4ae",
                    color: "#fff",
                    fontWeight: 500,
                  }}
                />
              ) : null
            )}
          </Box>
        )}
      </TableCell>

      {/* Actions */}
      <TableCell align="center">
        {multiEditMode ? (
          isRowDirty(dateStr) && (
            <Chip
              label="Modified"
              size="small"
              sx={{
                fontSize: "0.65rem",
                height: 20,
                backgroundColor: weekend ? "rgba(255,255,255,0.2)" : "#fff3e0",
                color: weekend ? "#fff" : "#e65100",
                fontWeight: "bold",
                border: weekend ? "1px solid rgba(255,255,255,0.4)" : "1px solid #ffcc80",
              }}
            />
          )
        ) : (
          <Box sx={{ display: "flex", flexDirection: "column", alignItems: "stretch", gap: 0.4 }}>
            {/* One grouped row per entry */}
            {entries.map((e) => {
              const halfLabel = e.halfDayType === "FIRST" ? "☀️" : e.halfDayType === "LAST" ? "🌙" : null;
              return (
                <Box
                  key={e.id}
                  sx={{
                    display: "flex",
                    alignItems: "center",
                    justifyContent: "center",
                    gap: 0.2,
                    borderRadius: 1,
                    px: 0.3,
                    backgroundColor: halfLabel
                      ? (e.halfDayType === "FIRST"
                          ? (weekend ? "rgba(255,253,231,0.15)" : "#fffde7")
                          : (weekend ? "rgba(227,242,253,0.15)" : "#e3f2fd"))
                      : "transparent",
                  }}
                >
                  {halfLabel && (
                    <span style={{ fontSize: "0.8rem", lineHeight: 1, flexShrink: 0 }}>{halfLabel}</span>
                  )}
                  {canEdit && (
                    <>
                      <Tooltip title="Edit">
                        <IconButton size="small" onClick={() => onEditEntry(e, date)} sx={{ p: 0.3, color: weekend ? "#fff" : "primary.main" }}>
                          <EditIcon sx={{ fontSize: 14 }} />
                        </IconButton>
                      </Tooltip>
                      <Tooltip title="Delete">
                        <IconButton size="small" onClick={() => onDeleteEntry(e.id)} sx={{ p: 0.3, color: weekend ? "#ffcdd2" : "error.main" }}>
                          <DeleteIcon sx={{ fontSize: 14 }} />
                        </IconButton>
                      </Tooltip>
                    </>
                  )}
                  {isManager && e.entryType === "ACTUAL" && (
                    <>
                      {e.approvalStatus !== "APPROVED" && (
                        <Tooltip title="Approve">
                          <IconButton size="small" onClick={() => onApproval(e.id, "APPROVED")} sx={{ p: 0.3, color: weekend ? "#a5d6a7" : "#2e7d32" }}>
                            <ApproveIcon sx={{ fontSize: 14 }} />
                          </IconButton>
                        </Tooltip>
                      )}
                      {e.approvalStatus !== "NOT_APPROVED" && (
                        <Tooltip title="Disapprove">
                          <IconButton size="small" onClick={() => onOpenDisapprove(e.id)} sx={{ p: 0.3, color: weekend ? "#ef9a9a" : "#c62828" }}>
                            <DisapproveIcon sx={{ fontSize: 14 }} />
                          </IconButton>
                        </Tooltip>
                      )}
                    </>
                  )}
                </Box>
              );
            })}
            {/* Add button when no entry, or one half-day slot is still free */}
            {canEdit && (entries.length === 0 || hasComplement) && (
              <Tooltip title={hasComplement ? "Add other half-day entry" : "Add entry"}>
                <IconButton size="small" onClick={() => onAddEntry(date)} sx={{ alignSelf: "center", color: weekend ? "#fff" : "primary.main" }}>
                  <AddIcon fontSize="small" />
                </IconButton>
              </Tooltip>
            )}
          </Box>
        )}
      </TableCell>
    </TableRow>
  );
});

export default React.memo(CalendarTable);
